import type { ProjectSummary } from "../types";
import { resolvePhotoUrl } from "../photo-storage";
import { escapeHtml, renderPagePath } from "../view-helpers";

type ReceivingSectionSummary = {
  label: string;
  stateLabel: string;
  warning?: boolean;
  detail: string;
};

function buildReceivingSummaries(project: ProjectSummary): ReceivingSectionSummary[] {
  const bodyReady = project.receiving.documentBody.trim().length > 0;
  const photoCount = project.receiving.photos.length;
  const photoReady = photoCount > 0;

  return [
    {
      label: "接件正文",
      stateLabel: bodyReady ? "已填写" : "待补充",
      warning: !bodyReady,
      detail: bodyReady ? "接件正文已经有内容，可继续按需要微调。" : "接件正文还没有补齐，报告“二、接件”正文目前还不完整。",
    },
    {
      label: "接件图片",
      stateLabel: photoReady ? `已上传 ${photoCount} 张` : "待上传",
      warning: !photoReady,
      detail: photoReady ? "接件图片已经留存，后续生成报告时会按接件图片插入。" : "接件图片还没有上传，后续报告接件部分暂时不会带图。",
    },
    {
      label: "下一步衔接",
      stateLabel: bodyReady ? "可去现场评定" : "建议先补正文",
      warning: !bodyReady,
      detail: bodyReady ? "接件信息已具备继续进入现场评定的基础条件。" : "建议先把接件正文补齐，再进入现场评定，避免后面还要回头补接件内容。",
    },
  ];
}

function renderPhotoList(project: ProjectSummary): string {
  if (!project.receiving.photos.length) {
    return `<p class="muted">还没有上传接件图片。</p>`;
  }

  return `
    <div class="photo-grid compact-grid">
      ${project.receiving.photos
        .map(
          (photo, index) => {
            const previewUrl = resolvePhotoUrl(photo);
            return `
            <article
              class="photo-card compact-photo-card reorderable-photo-card"
              data-photo-scope="receiving"
              data-photo-key="${escapeHtml(photo)}"
              data-photo-index="${index}"
            >
              <button
                type="button"
                class="preview-link preview-button"
                data-preview-image="${previewUrl}"
                data-preview-title="接件图片 ${index + 1}"
              >
                <img src="${previewUrl}" alt="接件图片 ${index + 1}" />
              </button>
              <button type="button" class="photo-delete-button" data-receiving-photo-delete="${index}" aria-label="删除图片">×</button>
            </article>
          `;
          },
        )
        .join("")}
    </div>
  `;
}

export function renderProjectReceivingPage(project: ProjectSummary): string {
  return `
    <section class="screen">
      <header class="topbar">
        <button class="ghost-button" data-action="back-to-home">返回项目</button>
        <h1>项目接件信息</h1>
      </header>
      ${renderPagePath([
        { label: project.name, href: `#/projects/home?id=${project.id}` },
        { label: "项目接件信息" },
      ])}
      <div class="panel stack">
        <p class="eyebrow">接件内容</p>
        <h2>${escapeHtml(project.name)}</h2>
        <p class="muted">接件正文按报告模板默认内容预填，可直接修改；接件图片需要现场重新上传。</p>
      </div>
      <form id="receiving-form" class="panel stack">
        <p class="mini-meta">输入时会自动保存到本地，弱网或断网也不会因为没点保存就丢掉当前文字。</p>
        <label class="field">
          <span>接件正文</span>
          <textarea
            name="documentBody"
            rows="8"
            placeholder="接件正文会按模板默认内容预填，可在此基础上修改"
          >${escapeHtml(project.receiving.documentBody)}</textarea>
        </label>
        <div class="field">
          <span>接件图片</span>
          <div class="photo-inputs">
            <label class="upload-chip">
              <span>图片上传</span>
              <input id="receiving-photo-gallery-input" name="receivingPhotos" type="file" accept="image/*" multiple />
            </label>
          </div>
          <p class="mini-meta" id="receiving-photo-upload-status">该入口支持多选；选择图片后会先处理压缩，再自动追加到当前接件信息里。部分手机也会在这里提供拍照入口。</p>
          ${renderPhotoList(project)}
        </div>
        <button class="nav-button" type="button" data-action="go-inspection-items">进入现场评定</button>
        <button class="primary-button" type="submit">保存接件信息</button>
      </form>
    </section>
  `;
}
